/**
 * Synthetic landmark generation.
 *
 * Lets the whole control chain — coordinates, identity, smoothing, mapping, audio — be
 * driven deterministically from unit tests and end-to-end tests without a camera or the
 * MediaPipe model. The geometry is a plausible flat hand, not an anatomically exact one:
 * what matters is that the palm centroid, fingertips and wrist land where a real detection
 * would put them relative to each other.
 */

import type { HandLandmarkSample, Handedness, NormalizedLandmark } from './types';
import { LANDMARK_COUNT } from './handFrameCodec';

export interface SyntheticHandOptions {
  /** Palm centre in image-normalized coordinates. */
  x: number;
  y: number;
  /** Hand size as a fraction of frame height. */
  scale?: number;
  /** Rotation of the hand about its palm, radians. */
  rotation?: number;
  handedness?: Handedness;
  score?: number;
  /** Relative depth written to every landmark. */
  z?: number;
  /** 0 = flat open hand (the bare template), 1 = closed fist. Drives the openness reading. */
  curl?: number;
}

/**
 * Landmark offsets from the palm centre in a unit-sized, upright, palm-forward hand.
 * Index order matches MediaPipe's hand topology.
 */
const TEMPLATE: readonly (readonly [number, number])[] = [
  [0, 0.55], // 0  wrist
  [-0.28, 0.42], // 1  thumb cmc
  [-0.42, 0.24], // 2  thumb mcp
  [-0.5, 0.06], // 3  thumb ip
  [-0.55, -0.1], // 4  thumb tip
  [-0.22, 0.02], // 5  index mcp
  [-0.26, -0.24], // 6  index pip
  [-0.28, -0.42], // 7  index dip
  [-0.3, -0.58], // 8  index tip
  [-0.04, 0.0], // 9  middle mcp
  [-0.05, -0.28], // 10 middle pip
  [-0.06, -0.48], // 11 middle dip
  [-0.07, -0.64], // 12 middle tip
  [0.14, 0.02], // 13 ring mcp
  [0.16, -0.24], // 14 ring pip
  [0.17, -0.42], // 15 ring dip
  [0.18, -0.57], // 16 ring tip
  [0.3, 0.08], // 17 pinky mcp
  [0.34, -0.12], // 18 pinky pip
  [0.36, -0.27], // 19 pinky dip
  [0.38, -0.4], // 20 pinky tip
];

/**
 * Which knuckle each finger joint curls towards, and how far along the finger it sits.
 *
 * Curling a finger is modelled as pulling each joint towards its own MCP knuckle, more
 * strongly the further out the joint is. That is not anatomically exact — a real finger
 * folds over itself — but it reproduces the property the app actually measures: the
 * fingertips end up next to the knuckles when the hand closes.
 */
const CURL_TARGETS: Readonly<Record<number, readonly [number, number]>> = {
  6: [5, 0.45],
  7: [5, 0.75],
  8: [5, 0.95],
  10: [9, 0.45],
  11: [9, 0.75],
  12: [9, 0.95],
  14: [13, 0.45],
  15: [13, 0.75],
  16: [13, 0.95],
  18: [17, 0.45],
  19: [17, 0.75],
  20: [17, 0.95],
};

/** Template point `index`, with `curl` applied. */
function curledTemplatePoint(index: number, curl: number): readonly [number, number] {
  const point = TEMPLATE[index] ?? [0, 0];
  const target = CURL_TARGETS[index];
  if (!target || curl <= 0) return point;
  const anchor = TEMPLATE[target[0]] ?? [0, 0];
  const t = Math.min(1, Math.max(0, curl)) * target[1];
  return [point[0] + (anchor[0] - point[0]) * t, point[1] + (anchor[1] - point[1]) * t];
}

/** Build one synthetic hand in image-normalized space. */
export function synthesizeHand(options: SyntheticHandOptions): HandLandmarkSample {
  const scale = options.scale ?? 0.22;
  const rotation = options.rotation ?? 0;
  const z = options.z ?? 0;
  const curl = options.curl ?? 0;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  const landmarks: NormalizedLandmark[] = [];
  for (let i = 0; i < LANDMARK_COUNT; i += 1) {
    const point = curledTemplatePoint(i, curl);
    const ox = point[0] * scale;
    const oy = point[1] * scale;
    landmarks.push({
      x: options.x + ox * cos - oy * sin,
      y: options.y + ox * sin + oy * cos,
      z,
    });
  }

  return {
    landmarks,
    handedness: options.handedness ?? 'Unknown',
    score: options.score ?? 0.95,
  };
}

/** Convenience for the common "two hands at these positions" case. */
export function synthesizeHands(specs: readonly SyntheticHandOptions[]): HandLandmarkSample[] {
  return specs.map((spec) => synthesizeHand(spec));
}
